/**
 * TradingSettingsManager - 交易设置管理器
 * 
 * 管理用户的交易设置（费率、杠杆、限制等）
 */

import type { TradingSettings } from '../types/tradingSettings.types';
import { DEFAULT_TRADING_SETTINGS } from '../types/tradingSettings.types';
import { storage } from '../utils/storage';
import { logger } from '../utils/logger';

const STORAGE_KEY_PREFIX = 'trading_settings_';

export class TradingSettingsManager {
  private settingsCache: Map<string, TradingSettings>;

  constructor() {
    this.settingsCache = new Map();
    logger.log('TradingSettingsManager initialized');
  }

  /**
   * 获取用户的交易设置
   */
  getSettings(uid: string): TradingSettings | null {
    // 先从缓存读取
    if (this.settingsCache.has(uid)) {
      return this.settingsCache.get(uid)!;
    }

    // 从存储加载
    const settings = storage.get<TradingSettings>(`${STORAGE_KEY_PREFIX}${uid}`);
    if (settings) {
      this.settingsCache.set(uid, settings);
      return settings;
    }

    return null;
  }

  /**
   * 创建默认交易设置
   */
  createDefaultSettings(uid: string, brokerUid: string, brokerName: string): TradingSettings {
    const settings: TradingSettings = {
      brokerUid,
      brokerName,
      assignedDate: Date.now(),
      ...DEFAULT_TRADING_SETTINGS,
      updatedAt: Date.now(),
      updatedBy: brokerUid,
    };

    this.saveSettings(uid, settings);
    logger.log(`✅ Created default trading settings for user ${uid}`);
    
    return settings;
  }

  /**
   * 保存交易设置
   */
  saveSettings(uid: string, settings: TradingSettings): void {
    // 更新时间戳
    settings.updatedAt = Date.now();

    // 保存到缓存
    this.settingsCache.set(uid, settings);

    // 保存到存储
    storage.set(`${STORAGE_KEY_PREFIX}${uid}`, settings);

    logger.log(`💾 Saved trading settings for user ${uid}`);
  }

  /**
   * 更新交易设置（部分更新）
   */
  updateSettings(
    uid: string, 
    updates: Partial<Omit<TradingSettings, 'updatedAt' | 'assignedDate'>>,
    updatedBy: string
  ): TradingSettings | null {
    const currentSettings = this.getSettings(uid);
    if (!currentSettings) {
      logger.error(`Cannot update settings: user ${uid} has no settings`);
      return null;
    }

    // 深度合并
    const updatedSettings: TradingSettings = {
      ...currentSettings,
      ...updates,
      fees: { ...currentSettings.fees, ...updates.fees },
      leverage: { ...currentSettings.leverage, ...updates.leverage },
      limits: { ...currentSettings.limits, ...updates.limits },
      riskControl: { ...currentSettings.riskControl, ...updates.riskControl },
      slippage: { ...currentSettings.slippage, ...updates.slippage },
      brokerRebate: { ...currentSettings.brokerRebate, ...updates.brokerRebate },
      updatedAt: Date.now(),
      updatedBy,
    };

    this.saveSettings(uid, updatedSettings);
    logger.log(`✅ Updated trading settings for user ${uid}`);

    return updatedSettings;
  }

  /**
   * 删除交易设置
   */
  deleteSettings(uid: string): void {
    this.settingsCache.delete(uid);
    storage.remove(`${STORAGE_KEY_PREFIX}${uid}`);
    logger.log(`🗑️ Deleted trading settings for user ${uid}`);
  }

  /**
   * 获取所有用户的交易设置
   */
  getAllSettings(): Map<string, TradingSettings> {
    // 从存储加载所有设置
    const allKeys = storage.keys().filter(key => key.startsWith(STORAGE_KEY_PREFIX));
    
    allKeys.forEach(key => {
      const uid = key.replace(STORAGE_KEY_PREFIX, '');
      if (!this.settingsCache.has(uid)) {
        const settings = storage.get<TradingSettings>(key);
        if (settings) {
          this.settingsCache.set(uid, settings);
        }
      }
    });

    return new Map(this.settingsCache);
  }

  /**
   * 按交易商UID获取所有客户的设置
   */
  getSettingsByBroker(brokerUid: string): Map<string, TradingSettings> {
    const allSettings = this.getAllSettings();
    const brokerSettings = new Map<string, TradingSettings>();

    allSettings.forEach((settings, uid) => {
      if (settings.brokerUid === brokerUid) {
        brokerSettings.set(uid, settings);
      }
    });

    return brokerSettings;
  }

  /**
   * 验证交易设置是否有效
   */
  validateSettings(settings: Partial<TradingSettings>): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // 验证费率
    if (settings.fees) {
      if (settings.fees.maker < 0 || settings.fees.maker > 0.01) {
        errors.push('Maker费率必须在 0-1% 之间');
      }
      if (settings.fees.taker < 0 || settings.fees.taker > 0.01) {
        errors.push('Taker费率必须在 0-1% 之间');
      }
      if (settings.fees.maker > settings.fees.taker) {
        errors.push('Maker费率不应高于Taker费率');
      }
      if (settings.fees.deposit !== undefined) {
        if (settings.fees.deposit < 0 || settings.fees.deposit > 0.2) {
          errors.push('充值费率必须在 0-20% 之间');
        }
      }
    }

    // 验证交易商回馈
    if (settings.brokerRebate) {
      if (settings.brokerRebate.rate < 0 || settings.brokerRebate.rate > 0.1) {
        errors.push('交易商回馈比例必须在 0-10% 之间');
      }
      // 如果同时有充值费率，确保回馈不超过费率
      if (settings.fees?.deposit !== undefined && settings.brokerRebate.rate > settings.fees.deposit) {
        errors.push('交易商回馈比例不应超过充值费率');
      }
    }

    // 验证杠杆
    if (settings.leverage) {
      if (settings.leverage.max < 1 || settings.leverage.max > 125) {
        errors.push('最大杠杆必须在 1-125 之间');
      }
    }

    // 验证交易限制
    if (settings.limits) {
      if (settings.limits.minOrderSize <= 0) {
        errors.push('最小交易量必须大于0');
      }
      if (settings.limits.maxOrderSize <= 0) {
        errors.push('最大交易量必须大于0');
      }
      if (settings.limits.minOrderSize > settings.limits.maxOrderSize) {
        errors.push('最小交易量不能大于最大交易量');
      }
    }

    // 验证风控
    if (settings.riskControl) {
      if (settings.riskControl.liquidationThreshold < 10 || settings.riskControl.liquidationThreshold > 100) {
        errors.push('强平维持率阈值必须在 10-100 之间');
      }
    }

    // 验证滑点
    if (settings.slippage) {
      if (settings.slippage.maxSlippage < 0 || settings.slippage.maxSlippage > 0.1) {
        errors.push('最大滑点必须在 0-10% 之间');
      }
      if (settings.slippage.probability < 0 || settings.slippage.probability > 100) {
        errors.push('滑点概率必须在 0-100 之间');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * 清空缓存
   */
  clearCache(): void {
    this.settingsCache.clear();
    logger.log('🧹 Cleared trading settings cache');
  }
}

